/**
 * The pages a link shows when it is not open — `docs/proposal.md` §9.2,
 * board 11.
 *
 * Four of the five states are not a document: they are a short card saying
 * what happened and one thing to do about it. The server decided which one
 * (`view.state`), so none of this computes anything from a date — a page that
 * works out "expired" in the browser is a page a wrong clock can sign.
 *
 * **They carry no prices, because the server sent none** (§9.2). The
 * projection returns no visits, no plans and no totals for expired, replaced
 * and declined, so there is nothing here to hide: these are exactly the
 * quotes whose figures are stale, and a stale price somebody screenshots is
 * worse than no price at all.
 *
 * The copy rule, from §9.4: the declined page **repeats the customer's own
 * reason** and never says "sorry to see you go" or asks them to reconsider.
 * Asking somebody to change their mind on the page where they said no is the
 * one thing that makes a decline feel like an argument.
 */
import { useState } from 'react'
import type { ProposalView } from '@rowted/shared'
import { formatDocumentDate } from '../../lib/format.js'
import { IconCheck } from '../../frame/icons.js'
import { cn } from '../../lib/cn.js'
import { GlyphChevronRight, GlyphClock, GlyphClose, GlyphInfo, GlyphPhone } from './glyphs.js'
import { Lockup } from './top-bar.js'

export interface StateProps {
  view: ProposalView
  /** Sends `POST …/reopen`; absent where there is nothing to ask for. */
  onReopen?: (note: string) => Promise<void>
}

/**
 * The card every closed state is made of.
 *
 * One shape, so a customer who declined in March and opens the link in June
 * reads the same furniture they read then — a different layout per state
 * would make each one feel like a different company.
 */
/**
 * Board 11's one shape for every closed page: a 64px circle with the state's
 * glyph, a 26px title, one paragraph, then the cards and the two buttons — the
 * one thing to do, and the phone number. Centred, because there is no longer a
 * document to read down; there is one answer to read.
 */
function StateCard({ tone, icon, title, children, after }: {
  tone: 'quiet' | 'warn'
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  after?: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-4 pt-[22px]">
      <div className="flex flex-col items-center gap-3 px-1.5 pt-[22px] text-center">
        <span className={cn('flex h-16 w-16 shrink-0 items-center justify-center rounded-full',
          tone === 'warn' ? 'bg-amber-band-bg text-amber-band-text' : 'bg-line-soft text-text-muted')}>
          {icon}
        </span>
        <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.1] tracking-[-0.025em] text-text">
          {title}
        </h1>
        <div className="text-[15px] leading-[1.5] text-text-body">{children}</div>
      </div>
      {after}
    </section>
  )
}

/** The document it was: the company's mark, the title, the reference and date. */
function WhichOne({ view }: { view: ProposalView }) {
  const date = view.quoteDate
    ? new Date(`${view.quoteDate.slice(0, 10)}T12:00:00`)
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null
  return (
    <div className="flex items-center gap-3.5 rounded-[18px] border border-line bg-surface px-[18px] py-4
                    shadow-proposal">
      <span className="flex shrink-0 items-center gap-2.5">
        <Lockup identity={view.identity} size={36} />
        {!view.identity.logoUrl && (
          <b className="whitespace-nowrap text-[14px] leading-[1.05] tracking-[-0.015em] text-text">
            {view.identity.name}
          </b>
        )}
      </span>
      <span className="ml-auto flex min-w-0 flex-col text-right">
        <b className="truncate text-[14px] text-text">{view.title}</b>
        <span className="text-[12.5px] text-text-faint">
          {view.reference}{date && ` · ${date}`}
        </span>
      </span>
    </div>
  )
}

/** The second button: a call, which never needs the page to work. */
function CallThem({ view }: { view: ProposalView }) {
  const phone = view.estimator?.phone ?? view.identity.phone
  if (!phone) return null
  return (
    <a href={`tel:${phone}`}
      className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[14px] border border-line
                 bg-surface text-[14.5px] font-semibold text-text">
      <GlyphPhone size={18} />
      {`Call ${phone}`}
    </a>
  )
}

const firstName = (view: ProposalView): string =>
  view.estimator?.name.trim().split(/\s+/)[0] ?? 'us'

function AskAgain({ label, placeholder, onReopen }: {
  label: string
  placeholder: string
  onReopen: (note: string) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  if (sent) {
    return (
      <p className="m-0 inline-flex items-center gap-2 text-[14px] font-semibold text-brand-tx">
        <IconCheck size={16} />Sent. They will be in touch.
      </p>
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-[54px] w-full items-center justify-center rounded-[14px] bg-brand text-[16px]
                   font-semibold text-brand-on"
      >
        {label}
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2.5">
      <textarea
        aria-label={label}
        value={note}
        rows={3}
        placeholder={placeholder}
        onChange={(event) => setNote(event.target.value)}
        className="w-full rounded-[12px] border-[1.6px] border-line bg-surface px-4 py-3.5
                   text-[15.5px] text-text focus:border-brand-ui focus:shadow-[0_0_0_4px_var(--ring)]
                   focus:outline-none"
      />
      <div className="flex gap-2.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            void onReopen(note.trim())
              .then(() => setSent(true))
              .finally(() => setBusy(false))
          }}
          className="flex h-[50px] flex-1 items-center justify-center rounded-[12px] bg-brand px-5
                     text-[14.5px] font-semibold text-brand-on disabled:opacity-60"
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex h-[50px] items-center rounded-[12px] border border-line-strong bg-surface px-[18px]
                     text-[14.5px] font-semibold text-text"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export function DeclinedPage({ view, onReopen }: StateProps) {
  /*
   * **It repeats nothing and argues with nothing** (§9.4): no "sorry to see
   * you go", no "are you sure", no second screen offering a discount. What
   * happened, that the office knows, and the one way back if they want it.
   */
  const who = firstName(view)
  return (
    <StateCard
      tone="quiet"
      icon={<GlyphClose size={29} />}
      title="You declined this proposal"
      after={(
        <>
          <WhichOne view={view} />
          {view.expiresOn && onReopen && (
            <div className="flex flex-col gap-1.5 rounded-[18px] border border-line bg-surface px-[18px] py-4
                            shadow-proposal">
              <b className="text-[15px] text-text">Changed your mind?</b>
              <span className="text-[14px] leading-[1.5] text-text-muted">
                {`Ask ${who} to open it again. Until ${shortDate(view.expiresOn)} the same prices are held for you.`}
              </span>
            </div>
          )}
          <div className="mt-1 flex flex-col gap-2.5">
            {onReopen && (
              <AskAgain
                label={`Ask ${who} to reopen it`}
                placeholder="Anything you would like changed?"
                onReopen={onReopen}
              />
            )}
            <CallThem view={view} />
          </div>
        </>
      )}
    >
      {`Thank you for telling us. ${who === 'us' ? 'We have' : `${who} has`} been notified.`}
    </StateCard>
  )
}

export function ExpiredPage({ view, onReopen }: StateProps) {
  const who = firstName(view)
  return (
    <StateCard
      tone="warn"
      icon={<GlyphClock size={29} />}
      title="This proposal has expired"
      after={(
        <>
          <WhichOne view={view} />
          <div className="mt-1 flex flex-col gap-2.5">
            {onReopen && (
              <AskAgain
                label={`Ask ${who} for a new proposal`}
                placeholder="Anything that has changed since?"
                onReopen={onReopen}
              />
            )}
            <CallThem view={view} />
          </div>
        </>
      )}
    >
      {view.expiresOn
        ? `It was good until ${formatDocumentDate(view.expiresOn)}.`
        : 'It is past its date.'}
      {` Prices and dates may have changed, so ${who === 'us' ? 'we' : who} will send you a fresh one.`}
    </StateCard>
  )
}

export function ReplacedPage({ view }: StateProps) {
  const who = firstName(view)
  /* Board 11 cell 5: an amber card at the top, the way forward inside it. */
  return (
    <section className="flex flex-col gap-4 pt-[22px]">
      <div className="flex flex-col gap-3.5 rounded-[18px] border border-amber-band-line bg-amber-band-bg
                      px-[18px] py-4 shadow-proposal">
        <div className="flex items-start gap-2.5">
          <GlyphInfo size={22} className="mt-px shrink-0 text-amber-band-text" />
          <div className="flex flex-col gap-[3px]">
            <h1 className="m-0 text-[16px] font-bold text-amber-band-text">
              {`${who === 'us' ? 'We' : who} sent you a newer version`}
            </h1>
            <span className="text-[14px] leading-[1.5] text-amber-band-text">
              This one was replaced. You cannot sign it any more, so nothing here can be accepted
              by mistake.
            </span>
          </div>
        </div>
        {view.replacedBy && (
          <a
            href={`/portal/quote/${view.replacedBy}`}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[14px] bg-brand
                       text-[14.5px] font-semibold text-brand-on"
          >
            Open the new proposal
            <GlyphChevronRight size={18} />
          </a>
        )}
      </div>
      <WhichOne view={view} />
      <CallThem view={view} />
    </section>
  )
}

/** "Nov 9" — board 11 says the day, not the year, in a sentence. */
const shortDate = (day: string): string =>
  new Date(`${day.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

export function closedPage(props: StateProps): React.ReactNode {
  if (props.view.state === 'declined') return <DeclinedPage {...props} />
  if (props.view.state === 'expired') return <ExpiredPage {...props} />
  if (props.view.state === 'replaced') return <ReplacedPage {...props} />
  return null
}
